import { PersonRequest } from "@plinknz/tah-website-elements";

import { bankConfig } from "./config-generics/bank-details";
import { metDetailsConfig } from "./config-generics/met";
import { personalDetailsConfig } from "./config-generics/personal-details";
import { signatureConfig } from "./config-generics/signature";
import {
  PageConfig,
  Question,
  SurveyConfig,
} from "../../components/content-blocks/forms/customer-form-types";

function createAcademicHistoryConfig(): PageConfig[] {
  const questions: Question[] = [
    {
      type: "matrixdynamic",
      name: "academicHistory",
      title: "Academic History",
      description: "Please fill in your academic history to date below",
      addRowText: "Add Row",
      columnMinWidth: "130px",
      cellType: "radiogroup",
      columns: [
        {
          name: "year",
          cellType: "text",
          title: "Year",
          isRequired: true,
        },
        {
          name: "institute",
          title: "Institute",
          cellType: "string",
          isRequired: true,
        },
        {
          name: "subject",
          title: "Subject",
          cellType: "string",
          isRequired: true,
        },
        {
          name: "grade",
          title: "Grade",
          cellType: "string",
          isRequired: true,
        },
      ],
      rowCount: 1,
      minRowCount: 1,
      maxRowCount: 100,
    },
  ];

  return [
    {
      name: "academicHistory",
      title: `Hītoria Ako - Academic History`,
      questions,
    },
  ];
}

function createWorkHistoryConfig(): PageConfig[] {
  const questions: Question[] = [
    {
      type: "matrixdynamic",
      name: "workHistory",
      title: "Work History",
      description:
        "Please provide details of your relevant work history, including any relevant voluntary and community work",
      addRowText: "Add Row",
      columnMinWidth: "130px",
      cellType: "radiogroup",
      columns: [
        {
          name: "year",
          cellType: "text",
          title: "Year",
          isRequired: true,
        },
        {
          name: "organisation",
          title: "Organisation",
          cellType: "string",
          isRequired: true,
        },
        {
          name: "duties",
          title: "Duties / Responsibilities",
          cellType: "string",
          isRequired: true,
        },
      ],
      rowCount: 1,
      minRowCount: 1,
      maxRowCount: 100,
    },
  ];

  return [
    {
      name: "workHistory",
      title: `Hītoria Mahi - Work History`,
      questions,
    },
  ];
}

export const createSurveyConfig = (member: PersonRequest): SurveyConfig => {
  const personalConfig = personalDetailsConfig(member);
  const introConfig = ([
    {
      name: "overview",
      title: "Manawa Energy Taiao Scholarship",
      elements: [
        {
          type: "html",
          html: `
<p>
  Te taiao is a key strategic priority for Te Rūnanga a Rangitāne o Wairau
  Trust. Rangitāne aims to ensure the integrity and sustainability of te taiao,
  for current and future generations. Taiao objectives for Rangitāne include the
  following:
  <ul>
    <li>
      Protect and restore key sites of significance such as Te Pokohiwi a Kupe,
      Wairau Lagoons, Grovetown Lagoons, and the Wairau Bar, while promoting
      their history.
    </li>
    <li>
      Advocate for sustainable practices in land use, fisheries, and the
      Marlborough Sounds, and ensure the recognition and exercise of Rangitāne
      customary rights.
    </li>
    <li>
      Prepare for climate change and its effects, supporting environmental
      sustainability and resilience.
    </li>
    <li>
      Revive traditional practices like Maara Kai and hunting/gathering, for the
      benefit of whānau hauora.
    </li>
  </ul>
</p>
<p>
  One avenue to achieve this is by investing in future Rangitāne Taiao Leaders
  by awarding the Manawa Energy Taiao Scholarship to up to two recipients per
  year.
</p>
      `,
        },
      ],
    },
    {
      name: "scholarship",
      title: "The Scholarship",
      elements: [
        {
          type: "html",
          html: `
<p>
  <ul>
    <li>
      The scholarship will be awarded to up to two recipients per year who meet
      the application criteria
    </li>
    <li>
      Successful applicants will receive $3,000 per year for up to three years
    </li>
    <li>
      In addition to the $3,000, there may be an additional contribution by the
      Māori Education Trust
    </li>
    <li>
      Applicants may also apply for the Rangitāne General
      Scholarship
    </li>
    <li>
      If the successful applicant is undertaking their studies part-time, they
      may receive a pro-rata amount of the scholarship funds
    </li>
  </ul>
</p>
      `,
        },
      ],
    },
    {
      name: "criteria",
      title: "Criteria",
      elements: [
        {
          type: "html",
          html: `
<p>
  Applicants must be
  <ul>
    <li>
      a registered iwi member
    </li>
    <li>
      enrolled in undergraduate tertiary study full time or part time
    </li>
    <li>
      majoring in an area of study related to te taiao, including (but not
      limited to) areas such as:
      <ul>
        <li>avifauna ecology</li>
        <li>ecological engineering</li>
        <li>energy studies</li>
        <li>environmental and/or resource management planning</li>
        <li>environmental law</li>
        <li>freshwater ecology</li>
        <li>geospatial data</li>
        <li>marine conservation</li>
        <li>pest management</li>
        <li>urban and regional planning</li>
        <li>among others</li>
      </ul>
    </li>
  </ul>
</p>
      `,
        },
      ],
    },
    {
      name: "closing",
      title: "Closing Date",
      elements: [
        {
          type: "html",
          html: `
<p>
  <ul>
    <li>
      Applications must be received by 5pm on 28 February
    </li>
    <li>
      Incomplete and late applications will not be accepted
    </li>
  </ul>
</p>
      `,
        },
      ],
    },
    {
      name: "beforeApplication",
      title: "Before You Apply",
      elements: [
        {
          type: "html",
          html: `
<p>
  Ensure you have the following to support your application:
  <ul>
    <li>
      Proof of bank account details
    </li>
    <li>
      A photograph of yourself only that may be used for promotional purposes
    </li>
    <li>
      Confirmation of course enrolment for the upcoming academic year
    </li>
    <li>
      A copy of your most recent course results
    </li>
  </ul>
</p>
      `,
        },
      ],
    },
    {
      name: "successfulApplicants",
      title: "Successful Applicants",
      elements: [
        {
          type: "html",
          html: `
<p>
  On receiving the Manawa Energy Taiao Scholarship, recipients agree to:
  <ul>
    <li>
      Attend the Te Rūnanga a Rangitāne o Wairau Trust AGM 
    </li>
    <li>
      Their photograph being used in promotional material by:
      <ul>
        <li>Te Rūnanga a Rangitāne o Wairau Trust</li>
        <li>Manawa Energy</li>
        <li>Māori Education Trust</li>
      </ul>
    </li>
  </ul>
</p>
      `,
        },
      ],
    },
  ] as unknown) as PageConfig[];
  const courseConfig = ([
    {
      name: "enrolment",
      title: "Enrolment Confirmation For Upcoming Year",
      questions: [
        {
          type: "text",
          name: "studentID",
          title: "Student ID",
          placeHolder: "Tūmau Ākonga",
          isRequired: true,
        },
        {
          type: "text",
          name: "institution",
          title: "Name of tertiary institution",
          placeHolder: "Whare Wānanga",
          isRequired: true,
          startWithNewLine: false,
        },
        {
          type: "text",
          name: "programme",
          title: "Programme of study",
          placeHolder: "Kaupapa ako",
          isRequired: true,
        },
        {
          type: "text",
          name: "course",
          title: "Name of course / degree",
          placeHolder: "Tohu ako",
          isRequired: true,
          startWithNewLine: false,
        },
        {
          type: "text",
          name: "hours",
          title: "Hours of study",
          placeHolder: "Ngā haora ako",
          isRequired: true,
        },
        {
          type: "text",
          name: "year",
          title: "Year of study",
          placeHolder: "Te Tauau",
          isRequired: true,
          startWithNewLine: false,
        },
      ],
    },
  ] as unknown) as PageConfig[];
  const academicHistoryConfig = createAcademicHistoryConfig();
  const workHistoryConfig = createWorkHistoryConfig();
  const aspirationsConfig = ([
    {
      name: "aspirations",
      title: "Your Hapū / Iwi Development Aspirations - Tōu Āpōpō",
      questions: [
        {
          type: "comment",
          name: "aspirations",
          title: `
          Provide a typed essay of between 500 and 700 words on what
          inspired you to select your course of study and what contribution
          you hope to make to Te Taiao and to Te Rūnanga a Rangitāne o
          Wairau Trust.
        `,
        },
      ],
    },
  ] as unknown) as PageConfig[];
  const metConfig = metDetailsConfig();
  const confirmationConfig = ([
    {
      name: "confirmation",
      title: "Kī Taurangi - Declaration",
      questions: [
        {
          type: "radiogroup",
          name: "correct",
          title: `
            I confirm that all information supplied in this application form is
            correct and true. I know that if the information supplied is
            incomplete or inaccurate my application will be invalid and
            declined, without any right of review.
          `,
          choices: ["Āe"],
          isRequired: true,
        },
        {
          type: "radiogroup",
          name: "photo",
          title: `
            If I am successful, I will supply the office with a brief personal
            profile and a photo within 12 weeks of course completion so that my
            experience/success may be shared with Rangitāne o Wairau whānui.
          `,
          choices: ["Āe"],
          isRequired: true,
        },
        {
          type: "radiogroup",
          name: "promotional",
          title: `
            If I am the successful recipient, I consent to this information
            being used in promotional material and communications by Te Rūnanga
            a Rangitāne o Wairau Trust, Manawa Energy, and the Māori Education
            Trust, for the purpose of promoting the scholarship programme.
          `,
          choices: ["Āe"],
          isRequired: true,
        },
        {
          type: "radiogroup",
          name: "retention",
          title: `
            If I am the successful applicant, I understand that the scholarship
            may be for up to 3 years. However, receipt of funds after the
            initial year is contingent on successfully completing and passing
            the course requirements for the year prior and proof of my grades as
            well as a short report of my year must be submitted to the Trust
            before further funds are given.
          `,
          choices: ["Āe"],
          isRequired: true,
        },
        signatureConfig[0].questions[0],
      ],
    },
  ] as unknown) as PageConfig[];
  const internshipConfig = ([
    {
      name: "internship",
      title: "Internship",
      questions: [
        {
          name: "internship",
          title:
            "I would like to be considered for an internship with Te Rūnanga a Rangitāne o Wairau Trust?",
          type: "radiogroup",
          choices: ["Āe", "Kāo"],
          isRequired: true,
        },
      ],
    },
  ] as unknown) as PageConfig[];

  const declarationConfig = ([
    {
      name: "declaration",
      title: "Declaration",
      questions: [
        {
          name: "declaration",
          title: "How your information will be used",
          type: "html",
          html: `
          <p>
            If your application is successful, information about you and/or the
            title of your initiative and the amount of funding granted may be
            published in the Rangitāne o Wairau Annual Report and may also be
            used in promotional material.
          </p>
          <p>
            By submitting this application, you authorise Rangitāne o Wairau to
            disclose information to, or obtain information from, any individual
            or organisation as necessary for the purpose of assessing your
            application.
          </p>
          <p>
            Your personal information will be handled in accordance with the
            Privacy Act 2020, which governs how personal information is
            collected, used, stored, and shared. We will not publish or share
            any commercial or confidential information without your written
            consent.
          </p>
          <p>
            When your information is no longer required, it will be securely
            archived or destroyed in line with our policies and processes.
          </p>
          <p>
            If you have any questions or concerns about the information we hold
            about you, please contact us at
            <a href="mailto:admin@rangitane.org.nz" target="_blank">
              admin@rangitane.org.nz
            </a>
          </p>
          `,
        },
      ],
    },
  ] as unknown) as PageConfig[];

  return {
    pages: [
      ...introConfig,
      ...personalConfig,
      ...bankConfig,
      ...courseConfig,
      ...academicHistoryConfig,
      ...workHistoryConfig,
      ...aspirationsConfig,
      ...metConfig,
      ...internshipConfig,
      ...declarationConfig,
      ...confirmationConfig,
    ],
    completedHtml: `
      <h3>Tino Pai - Thank you for your application</h3>
      <p>
        The application has now been received by our office and will go through
        our review process. The Rūnanga will contact you shortly after, with the
        outcome.
      </p>
      <p>
        If you have any queries, please do not hesitate to contact the office
        via <a href="mailto:admin@rangitane.org.nz" target="_blank">
          admin@rangitane.org.nz
        </a>
      </p>
      `,
  };
};
